// frontend/src/site/builder/SiteContentEditor.jsx
import React, { useMemo } from 'react';

import HeroForm from '../builder/sections/HeroForm';
import SobreForm from '../builder/sections/SobreForm';
import ImoveisDestaqueForm from '../builder/sections/ImoveisDestaqueForm';
import ServicosForm from '../builder/sections/ServicosForm';
import ProvaSocialForm from '../builder/sections/ProvaSocialForm';
import CtaFinalForm from '../builder/sections/CtaFinalForm';
import RodapeForm from '../builder/sections/RodapeForm';

// ✅ ETAPA 03: seções por modelo
import { getModelSections, resolveModelId } from './siteSections';

/**
 * SiteContentEditor
 * - Renderiza os formulários por seção, respeitando:
 *   1) estrutura (ordem + ativo)
 *   2) conteudo (dados)
 *   3) ✅ modelId (seções permitidas)
 */
export default function SiteContentEditor({
  modelId = 'corretor',
  estrutura = [],
  conteudo = {},
  onChange,
  debug = false,
}) {
  const mid = useMemo(() => resolveModelId(modelId), [modelId]);

  const allowedByModel = useMemo(() => {
    const secs = getModelSections(mid);
    return new Set(secs.map((s) => s.id));
  }, [mid]);

  const orderedActiveSections = useMemo(() => {
    const arr =
      Array.isArray(estrutura) && estrutura.length
        ? estrutura
        : getModelSections(mid);

    return (
      arr
        .filter((s) => s && s.ativo !== false)
        .map((s) => ({ ...s, id: String(s?.id || '').trim() }))
        .filter((s) => !!s.id)
        // ✅ modelo manda: se não estiver permitido, nem aparece
        .filter((s) => allowedByModel.has(s.id))
    );
  }, [estrutura, mid, allowedByModel]);

  function setSection(sectionKey, patch) {
    if (typeof onChange !== 'function') return;

    const prev = conteudo && typeof conteudo === 'object' ? conteudo : {};
    const prevSection =
      prev[sectionKey] && typeof prev[sectionKey] === 'object'
        ? prev[sectionKey]
        : {};

    const next = {
      ...prev,
      [sectionKey]: {
        ...prevSection,
        ...(patch || {}),
      },
    };

    onChange(next);
  }

  const registry = useMemo(
    () => ({
      hero: { title: 'Hero Section', key: 'hero', Component: HeroForm },

      // corretor
      sobre: { title: 'Sobre o Corretor', key: 'sobre', Component: SobreForm },
      imoveis: {
        title: 'Imóveis em Destaque',
        key: 'imoveis',
        Component: ImoveisDestaqueForm,
      },
      imoveisDestaque: {
        title: 'Imóveis em Destaque',
        key: 'imoveis',
        Component: ImoveisDestaqueForm,
      },

      servicos: { title: 'Serviços', key: 'servicos', Component: ServicosForm },
      provaSocial: {
        title: 'Prova Social',
        key: 'provaSocial',
        Component: ProvaSocialForm,
      },
      depoimentos: {
        title: 'Prova Social',
        key: 'provaSocial',
        Component: ProvaSocialForm,
      },

      // ✅ imobiliaria (ETAPA 03)
      destaques: {
        title: 'Destaques (máx 6)',
        key: 'destaques',
        Component: ImoveisDestaqueForm, // reaproveita form (ele já entende modo/ids/quantidade)
      },
      terrenos: {
        title: 'Terrenos (últimos 6)',
        key: 'terrenos',
        Component: ImoveisDestaqueForm, // reaproveita (você pode criar TerrenosForm depois)
      },

      ctaFinal: {
        title: 'Chamada para Ação Final',
        key: 'ctaFinal',
        Component: CtaFinalForm,
      },
      contato: {
        title: 'Chamada para Ação Final',
        key: 'ctaFinal',
        Component: CtaFinalForm,
      },

      rodape: { title: 'Rodapé', key: 'rodape', Component: RodapeForm },
      footer: { title: 'Rodapé', key: 'rodape', Component: RodapeForm },
    }),
    []
  );

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-base font-semibold text-gray-900">
            Conteúdo do Site
          </h3>
          <p className="text-sm text-gray-600">
            Modelo ativo: <b className="font-medium">{mid}</b>
          </p>

          {debug ? (
            <div className="mt-2 text-xs text-gray-500">
              Seções ativas:{' '}
              <span className="font-medium">
                {orderedActiveSections.map((s) => s.id).join(', ') || '—'}
              </span>
            </div>
          ) : null}
        </div>
      </div>

      {orderedActiveSections.length === 0 ? (
        <div className="rounded-xl border bg-white p-4">
          <p className="text-sm text-gray-700">
            Nenhuma seção ativa para este modelo.
          </p>
        </div>
      ) : (
        orderedActiveSections.map((sec, idx) => {
          const id = String(sec?.id || '').trim();
          const entry = registry[id];

          if (!entry) {
            return (
              <div
                key={`${id || 'unknown'}_${idx}`}
                className="rounded-2xl border bg-white p-5"
              >
                <div className="flex items-center justify-between gap-3">
                  <h4 className="text-sm font-semibold text-gray-900">
                    Seção não reconhecida
                  </h4>
                  <span className="text-xs text-gray-500">
                    id: <b className="font-medium">{id || '(vazio)'}</b>
                  </span>
                </div>
                <div className="mt-2 text-sm text-gray-600">
                  Essa seção existe na estrutura, mas não foi registrada no
                  SiteContentEditor.
                </div>
              </div>
            );
          }

          const { title, key, Component } = entry;

          const safeConteudo =
            conteudo && typeof conteudo === 'object' ? conteudo : {};
          const value =
            safeConteudo[key] && typeof safeConteudo[key] === 'object'
              ? safeConteudo[key]
              : {};

          return (
            <div
              key={`${id}_${idx}`}
              className="rounded-2xl border bg-white p-5"
            >
              <div className="mb-4">
                <div className="flex items-center justify-between gap-3">
                  <h4 className="text-sm font-semibold text-gray-900">
                    {title}
                  </h4>

                  <div className="flex items-center gap-3">
                    {sec?.layout ? (
                      <span className="text-xs text-gray-500">
                        Layout:{' '}
                        <b className="font-medium">{String(sec.layout)}</b>
                      </span>
                    ) : null}

                    <span className="text-xs text-gray-400">
                      id: <b className="font-medium">{id}</b>
                    </span>
                  </div>
                </div>
                <div className="mt-2 h-px w-full bg-gray-100" />
              </div>

              <Component
                value={value}
                onChange={(patch) => setSection(key, patch)}
                section={sec}
                sectionId={id}
                modelId={mid}
              />
            </div>
          );
        })
      )}
    </div>
  );
}
